import { promotionalCampaignDAO } from '@niarab2c/frontend-commons/src/daos';
import { HotelResult } from '@niarab2c/frontend-commons/src/types/hotels';
import callApi from '@niarab2c/frontend-commons/src/util/callApi';
import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import _debounce from 'lodash/debounce';
import _isEqual from 'lodash/isEqual';
import { nanoid } from 'nanoid';
import { HotelPostSearchRule, HotelSearchRule } from 'niara-spear-crudmodel';
import type { AnyAction } from 'redux';
import type { BaseRootState, CompleteRootState, Listener } from '../../base';
import type { HotelDetailsState } from './hotelDetails';
import hotelDetails, { setFilter } from './hotelDetails';
import type { HotelPaginationState } from './hotelPagination';
import * as HotelPagination from './hotelPagination';
import type { CriteriaForm, State as HotelSearchState } from './hotelSearch';
import hotelSearch, { searchHotels as _searchHotels } from './hotelSearch';
export { setFilter as setHotelDetailsFilter };
export * as HotelDetails from './hotelDetails';
export * as HotelPagination from './hotelPagination';
export * as HotelSearch from './hotelSearch';
export type HotelState = {
  hotelSearch: HotelSearchState;
  hotelPagination: HotelPaginationState;
  hotelDetails: HotelDetailsState;
  /**
   * Pode receber o valor da regra hotelSearchRule ou group_hotelSearchRule
   */
  hotelSearchRule?: Partial<HotelSearchRule>;
  /**
   * Pode receber o valor da regra hotelPostSearchRule ou group_hotelPostSearchRule
   */
  hotelPostSearchRule?: Partial<HotelPostSearchRule>;
  searchId?: string;
  promotionalCampaignRule?: PromotionalCampaignRuleState;
  isForGroups?: boolean;
};
export type Content = {
  button_link?: string;
  button_text?: string;
  image?: string;
  text?: string;
  title?: string;
  criteria?: {
    destinationName?: string;
    destinations?: string;
    startDate?: string;
    endDate?: string;
    nights?: number;
    promoCode?: string;
    rooms?: number;
  };
};
export type SearchBanner = {
  image?: string;
  overlay?: boolean;
  text?: string;
  title?: string;
};
export type Campaign = {
  id?: string;
  name?: string;
  enabled?: boolean;
  startDate?: string;
  endDate?: string;
  content?: Content[];
  search_banner?: SearchBanner[];
};
export type PromotionalCampaigns = {
  id: string;
  name: string;
};
export interface PromotionalCampaignRuleState {
  id?: string;
  name?: string;
  enabled?: boolean;
  promotionalCampaigns?: PromotionalCampaigns[];
  unifyContent?: Content[];
  unifySearchBanner?: SearchBanner[];
}
export const loadHotelSearchRule = createAsyncThunk<{
  hotelSearchRule?: HotelSearchRule;
  hotelPostSearchRule?: HotelPostSearchRule;
  isForGroups?: boolean;
}, {
  clientId?: string;
  propertyId?: string;
  isForGroups?: boolean;
} | void, {
  state: CompleteRootState;
}>('hotel/loadHotelSearchRule', async (options, {
  getState
}) => {
  const rootState = getState();
  const clientId = (options && options?.clientId) ?? rootState.core?.clientId;
  const propertyId = options ? options?.propertyId : undefined;
  const landingPageId = rootState.storefrontConfig?.storefront?.landingPageId;
  const isForGroups = (options && options?.isForGroups) ?? rootState?.hotel?.isForGroups;
  const results = await callApi('niara-spear-commons', `/genericRuleResolver/details`, 'get', {
    params: {
      clientId,
      landingPageId,
      propertyId,
      isForGroups
    } // TODO complementar o contexto com mais dados (hotel, data, etc)
  });
  return {
    hotelSearchRule: results?.hotelSearchRule,
    hotelPostSearchRule: results?.hotelPostSearchRule
  };
});
const initialState: HotelState = {
  hotelSearch: hotelSearch(undefined, {
    type: 'IGNORE'
  }),
  hotelPagination: HotelPagination.default(undefined, {
    type: 'IGNORE'
  }),
  hotelDetails: hotelDetails(undefined, {
    type: 'IGNORE'
  })
};
const DEFAULT_PAGE_SIZE = 15;
const hotelSlice = createSlice({
  name: 'hotel',
  initialState,
  reducers: {
    _setHotelSearch: (state, action: PayloadAction<{
      hotelSearch: HotelSearchState;
    }>) => {
      const hotelSearch = action.payload.hotelSearch;
      state.hotelSearch = hotelSearch;
      state.searchId = hotelSearch.searchId;
      let hotelPagination = HotelPagination.reducer(undefined, HotelPagination.setResults({
        results: hotelSearch.results,
        hotelSearch
      }));
      if (hotelSearch?.criteria?.promoCode?.length > 0) {
        // se pesquisou com promoCode, seta automaticamente o filtro do promoCode, para mostrar apenas
        // os resultados com o promoCode solicitado
        hotelPagination = HotelPagination.reducer(hotelPagination, HotelPagination.setFilter({
          filter: {
            promoCode: hotelSearch?.criteria?.promoCode
          }
        }));
      }
      state.hotelPagination = hotelPagination;
    },
    setIsForGroups: (state, action: PayloadAction<boolean>) => {
      state.isForGroups = action?.payload;
    },
    clearSearch: state => {
      state.hotelSearch = {};
      state.hotelPagination = HotelPagination.initialState;
    }
  },
  extraReducers: builder => {
    builder.addCase(loadHotelSearchRule.fulfilled, (state, action) => {
      const previousHotelPagination = state.hotelPagination;
      const pageSizeFromRule = action.payload?.hotelPostSearchRule?.hotelAmountPerPage ?? DEFAULT_PAGE_SIZE;
      if (action.payload) {
        return {
          ...state,
          hotelSearchRule: action.payload?.hotelSearchRule,
          hotelPostSearchRule: action.payload?.hotelPostSearchRule,
          hotelPagination: {
            ...previousHotelPagination,
            pageSize: pageSizeFromRule
          }
        };
      } else {
        return state;
      }
    });
    builder.addCase(loadPromotionalCampaignRule.fulfilled, (state, action) => {
      if (action.payload) {
        return {
          ...state,
          promotionalCampaignRule: action.payload?.promotionalCampaignRule
        };
      } else {
        return state;
      }
    });
    builder.addMatcher((action: AnyAction) => action.type.startsWith('hotelSearch'), (state, action) => {
      if (!state.searchId && action.type.startsWith('hotelSearch/resumeCheckout') || state.searchId === action.payload?.searchId || state.searchId === action.meta?.arg?.searchId) {
        // const previousResults = state.hotelSearch?.results
        state.hotelSearch = hotelSearch(state.hotelSearch, action);

        // substituído pelo listener
        // if (previousResults !== state.hotelSearch?.results) {
        //   state.hotelPagination = HotelPagination.reducer(
        //     state.hotelPagination,
        //     HotelPagination.setResults({ results: state.hotelSearch.results })
        //   )
        // }
      }
      // hotel details encapsula um hotelSearch
      state.hotelDetails = hotelDetails(state.hotelDetails, action);
    });
    builder.addMatcher((action: AnyAction) => action.type.startsWith('hotelPagination'), (state, action) => {
      state.hotelPagination = HotelPagination.reducer(state.hotelPagination, action);
    });
    builder.addMatcher((action: AnyAction) => action.type.startsWith('hotelDetails'), (state, action) => {
      state.hotelDetails = hotelDetails(state.hotelDetails, action);
    });
  }
});
const {
  _setHotelSearch
} = hotelSlice.actions;
export const {
  clearSearch,
  setIsForGroups
} = hotelSlice.actions;
export default hotelSlice.reducer;
export const searchHotels = createAsyncThunk<{
  results: HotelResult[];
  searchId: string;
  errorMessage?: string;
}, {
  criteriaForm: CriteriaForm;
  engineRuleVersion: string;
  isForGroups?: boolean;
}, {
  state: BaseRootState;
  rejectValue: {
    searchId: string;
    errorMessage: string;
  };
}>('hotel/searchHotels', async ({
  criteriaForm,
  engineRuleVersion,
  isForGroups: paramIsForGroup
}, {
  getState,
  dispatch,
  rejectWithValue
}) => {
  // compara o critério - se não mudou, não faz nova busca
  const currentCriteria = getState().hotel?.hotelSearch?.criteria;
  const isForGroups = getState().hotel?.isForGroups ?? paramIsForGroup;
  if (currentCriteria && _isEqual(currentCriteria, criteriaForm)) {
    // keep previous search
  } else if (criteriaForm) {
    dispatch(clearSearch());
    const searchId = nanoid();
    dispatch(_setHotelSearch({
      hotelSearch: {
        searchId,
        performance: []
      }
    }));
    //carrega as regras de pesquisa
    await dispatch(loadHotelSearchRule({
      clientId: criteriaForm.clientId,
      propertyId: criteriaForm.destinations?.propertyId,
      isForGroups
    }));
    try {
      return await dispatch(_searchHotels({
        criteriaForm,
        searchId,
        roomRatesLimit: 1,
        engineRuleVersion,
        analyticsEventType: 'hotelAvailability',
        isForGroups
      })).then(unwrapResult);
    } catch (error) {
      return rejectWithValue({
        searchId,
        errorMessage: error.errorMessage
      });
    }
  }
});
export const createListener = (): Listener<BaseRootState> => {
  const debouncedDispatchSetResults = _debounce((results, dispatch, currentState) => {
    dispatch(HotelPagination.setResults({
      results,
      hotelSearch: currentState?.hotel?.hotelSearch
    }));
  }, 400, {
    maxWait: 1200,
    leading: true
  });
  return (previousState, currentState, dispatch) => {
    if (previousState?.hotel?.hotelSearch?.results != currentState?.hotel?.hotelSearch?.results) {
      // atualiza hotelPagination de acordo com atualização com results, mas usa o debounce para
      // diminuir as atualizações
      debouncedDispatchSetResults(currentState?.hotel?.hotelSearch?.results, dispatch, currentState);
    }
  };
};
export const loadPromotionalCampaignRule = createAsyncThunk<{
  promotionalCampaignRule?: PromotionalCampaignRuleState;
}, {
  clientId?: string;
} | void, {
  state: CompleteRootState;
}>('hotel/loadPromotionalCampaignRule', async (options, {
  getState
}) => {
  const rootState = getState();
  const clientId = (options && options?.clientId) ?? rootState.core?.clientId;
  const results = await callApi('niara-spear-commons', `/genericRuleResolver/details`, 'get', {
    params: {
      clientId
    }
  });
  if (results?.promotionalCampaignRule?.enabled) {
    const now = new Date();

    // função para ajustar a data para o início ou fim do dia
    const adjustDate = (dateStr: string, endOfDay = false): Date => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      if (endOfDay) date.setHours(23, 59, 59, 999);else date.setHours(0, 0, 0, 0);
      return date;
    };
    const promotionalCampaigns = await Promise.all(results.promotionalCampaignRule.promotionalCampaigns.map(async promo => {
      const campaign = await promotionalCampaignDAO.findById(promo.id);
      const hasValidStartDate = !campaign.startDate || adjustDate(campaign.startDate) <= now;
      const hasValidEndDate = !campaign?.endDate || adjustDate(campaign.endDate, true) >= now;
      const isValid = campaign?.enabled && hasValidStartDate && hasValidEndDate;
      if (!isValid) return null;
      return {
        ...promo,
        campaign
      };
    }));
    const validPromotionalCampaigns = promotionalCampaigns.filter(Boolean);
    const campaigns = validPromotionalCampaigns.map(p => p.campaign);
    const unifyContent = campaigns.flatMap(c => c?.content || []);
    const unifySearchBanner = campaigns.flatMap(c => c?.search_banner || []);
    results.promotionalCampaignRule = {
      ...results.promotionalCampaignRule,
      promotionalCampaigns: validPromotionalCampaigns,
      unifyContent,
      unifySearchBanner
    };
    return {
      promotionalCampaignRule: results.promotionalCampaignRule
    };
  }
  return null;
});